import EmailOutlined from "@mui/icons-material/EmailOutlined";
import NotificationAddOutlinedIcon from "@mui/icons-material/NotificationAddOutlined";
import PeopleOutlined from "@mui/icons-material/PeopleOutlined";
import Remove from "@mui/icons-material/Remove";
import {
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Dialog } from "components/Dialog";
import { getFullName } from "encamp-shared/src/utils/name";
import { AssigneePickerFragment } from "generated-graphql/graphql";
import { compact, uniqBy } from "lodash-es";
import pluralize from "pluralize";
import React, { useCallback, useMemo, useState } from "react";
import {
  Control,
  useController,
  useFieldArray,
  useWatch,
} from "react-hook-form";
import { v4 } from "uuid";
import { TaskFormType } from "./types";

type ReminderDialogProps = {
  open: boolean;
  onClose: () => void;
  control: Control<TaskFormType>;
};

export const ReminderDialog: React.FC<ReminderDialogProps> = ({
  control,
  onClose,
  open,
}) => {
  const watchers = useWatch<TaskFormType>({
    control,
    name: "watchers",
  });
  const assignee = useWatch({
    control,
    name: "assignee",
  });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "reminderDays",
    keyName: "_id",
  });

  const [editingId, setEditingId] = useState<string | null>(null);

  const handleAddReminder = useCallback(() => {
    const newId = v4();
    append({ days: 3, id: newId }, { shouldFocus: true });
    setEditingId(newId);
  }, [append]);

  const handleDeleteReminder = useCallback(
    (id: string) => {
      const idx = fields.findIndex((field) => field.id === id);
      if (editingId === id) {
        setEditingId(null);
      }
      remove(idx);
    },
    [editingId, fields, remove]
  );

  const onChange = useCallback(
    (value: string) => {
      if (editingId == null) return false;
      const idx = fields.findIndex((field) => field.id === editingId);
      if (idx === -1) return false;

      const numberValue = parseInt(value);
      if (!isNaN(numberValue)) {
        update(idx, { days: numberValue, id: editingId });
      }
      return true;
    },
    [editingId, fields, update]
  );

  const handleSave = useCallback((id: string) => {
    setEditingId(null);
  }, []);

  const recipientList = useMemo(() => {
    const watchersArray = Array.isArray(watchers) ? watchers : [watchers];

    return uniqBy(
      compact([...watchersArray, assignee]),
      "id"
    ) as AssigneePickerFragment[];
  }, [watchers, assignee]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Set reminders"
      description="Reminders will be sent to all assignees and watchers of this task"
      contentSx={{ paddingTop: 0 }}
      noPrimaryAction
      secondaryActionElements={
        fields.length > 0
          ? [
              <IconButton
                key="add-reminder"
                color="primary"
                onClick={handleAddReminder}
              >
                <Tooltip title="Add Reminder">
                  <NotificationAddOutlinedIcon />
                </Tooltip>
              </IconButton>,
            ]
          : []
      }
    >
      <Stack direction="column">
        <Stack gap={2}>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <PeopleOutlined color="primary" sx={{ fontSize: "16px" }} />
            <Typography variant="body2">Recipients</Typography>
          </Stack>
          <List sx={{ pt: 0, mx: 4 }}>
            {recipientList.map((recipient) => (
              <ListItem key={recipient.id} disablePadding>
                <ListItemText
                  primary={getFullName(recipient.person) || recipient.email}
                  secondary={
                    getFullName(recipient.person) ? recipient.email : undefined
                  }
                />
              </ListItem>
            ))}
          </List>
        </Stack>
        <Divider sx={{ my: 2 }} />

        <Stack gap={2}>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <EmailOutlined color="primary" sx={{ fontSize: "16px" }} />
            <Typography variant="body2">Reminders</Typography>
          </Stack>
          {fields?.length === 0 && (
            <Stack justifyContent="center" alignItems="center" gap={2} mb={2}>
              <Typography variant="body2">
                There are currently no reminders for this task
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleAddReminder}
                startIcon={<NotificationAddOutlinedIcon />}
              >
                Add Reminder
              </Button>
            </Stack>
          )}
          <Stack mx={4} gap={1}>
            {fields.map((field, index) => (
              <ReminderItem
                control={control}
                index={index}
                key={field.id}
                handleDelete={() => handleDeleteReminder(field.id)}
                handleEdit={() => setEditingId(field.id)}
                handleSave={() => handleSave(field.id)}
                onChange={onChange}
              />
            ))}
          </Stack>
        </Stack>
      </Stack>
    </Dialog>
  );
};

const ReminderItem: React.FC<{
  control: Control<TaskFormType>;
  index: number;
  handleDelete: () => void;
  handleEdit: () => void;
  handleSave: () => void;
  onChange: (value: string) => boolean;
}> = ({
  index,
  control,

  handleDelete,
  handleEdit,
  handleSave,
  onChange,
}) => {
  const { field } = useController({
    control,
    name: `reminderDays.${index}.days`,
  });

  const days = useWatch({
    control,
    name: `reminderDays.${index}.days`,
  });

  return (
    <Stack direction="row" justifyContent="space-between">
      <Stack direction="row" alignItems="center" gap={1}>
        <Typography>Send email</Typography>
        <TextField
          required
          name={`reminderDays.${index}.days`}
          value={days}
          hiddenLabel
          size="small"
          type="number"
          inputMode="numeric"
          variant="filled"
          slotProps={{ input: { inputProps: { min: 1 } } }}
          sx={{ width: 60 }}
          onFocus={handleEdit}
          onChange={(e) => {
            handleEdit();
            field.onChange(e.target.value);
            onChange(e.target.value);
          }}
          onBlur={handleSave}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSave();
            }
          }}
        />
        <Typography>{pluralize("day", days)} before due date</Typography>
      </Stack>

      <IconButton edge="end" size="small" onClick={handleDelete}>
        <Remove color="primary" />
      </IconButton>
    </Stack>
  );
};
